/**
 * DEV-328: single commission summary used by Agency > Commissions and
 * /my-commissions. Both pages used to recompute totals client-side with
 * different rules (and the agency total ran a serial per-client waterfall);
 * this module is the one implementation. Pure: the route loads rows, this
 * does the math.
 *
 * Definitions (both pages):
 * - record amount   = actualCommission when recorded, else projectedCommission
 * - record month    = accountingMonth, else month (YYYY-MM)
 * - agent share     = amount x client agency/agent split x (primary/secondary
 *                     split when more than one producer was assigned at the
 *                     end of the record month)
 * - growth          = same-store, total-over-total month change, averaged
 *                     geometrically; a client only counts from its first full
 *                     month after onboarding/import and only when it had book
 *                     in the earlier month (new book is not "growth")
 */

export type SummaryRecord = {
  id: number;
  clientId: number;
  month?: string | null;
  accountingMonth?: string | null;
  projectedCommission?: string | number | null;
  actualCommission?: string | number | null;
  status?: string | null;
};

export type SummaryAssignment = {
  clientId: number;
  userId: string;
  role?: string | null;
  effectiveDate?: string | Date | null;
  endDate?: string | Date | null;
  primarySecondarySplitId?: number | null;
};

export type SummaryClient = {
  id: number;
  agencyAgentSplitId?: number | null;
  clientSince?: string | Date | null;
  createdAt?: string | Date | null;
};

export type SummarySplitProfile = {
  id: number;
  splitType: string;
  agentPercentage?: string | number | null;
  primaryPercentage?: string | number | null;
  secondaryPercentage?: string | number | null;
  isDefault?: boolean | null;
};

export type RecordShare = {
  recordId: number;
  clientId: number;
  userId: string;
  month: string;
  /** Agent's dollars for this record. */
  share: number;
  /** Client agency/agent split, agent side (e.g. 60). */
  agentPct: number;
  /** Primary/secondary percentage when the record was split, else null. */
  splitPct: number | null;
  isSecondary: boolean;
};

export type AgentSummary = {
  userId: string;
  totalCommissions: number;
  monthTotal: number;
  recordCount: number;
  assignedClientCount: number;
};

export type CommissionSummary = {
  scope: "agency" | "agent";
  month: string;
  asOf: string;
  totalCommissions: number;
  monthTotal: number;
  pendingTotal: number;
  monthRecordCount: number;
  assignedClientCount: number;
  activeAgentCount: number;
  avgMonthlyGrowthPct: number | null;
  monthlyTotals: Array<{ month: string; total: number }>;
  shares: RecordShare[];
  agents: AgentSummary[];
};

const MONTH_RE = /^\d{4}-\d{2}$/;
const YMD_RE = /^(\d{4}-\d{2}-\d{2})/;

export function parseMoney(value: unknown): number {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const n = parseFloat(String(value).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

const hasValue = (v: unknown) => v !== null && v !== undefined && v !== "";

export function recordAmount(r: SummaryRecord): number {
  return parseMoney(hasValue(r.actualCommission) ? r.actualCommission : r.projectedCommission);
}

export function recordMonth(r: SummaryRecord): string | null {
  const m = String(r.accountingMonth || r.month || "");
  return MONTH_RE.test(m) ? m : null;
}

/**
 * Calendar date (YYYY-MM-DD) of a date-only string or a DB timestamp. Drizzle
 * maps `timestamp without time zone` to a Date at UTC, so UTC parts are the
 * stored calendar day regardless of the server's zone.
 */
export function toYmd(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
  }
  const m = YMD_RE.exec(String(value));
  return m ? m[1] : null;
}

function lastDayOfMonth(month: string): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month}-${String(d).padStart(2, "0")}`;
}

function addMonths(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** First full calendar month on/after a date: the 1st counts as full. */
export function firstFullMonth(date: string | Date | null | undefined): string | null {
  const ymd = toYmd(date);
  if (!ymd) return null;
  const month = ymd.slice(0, 7);
  return ymd.endsWith("-01") ? month : addMonths(month, 1);
}

const isProducer = (a: SummaryAssignment) => String(a.role || "").toLowerCase().includes("producer");
const isSecondaryRole = (a: SummaryAssignment) => String(a.role || "").toLowerCase().includes("secondary");

/** Assigned on the last day of the month — one owner per commission month. */
export function wasActiveAtMonthEnd(a: SummaryAssignment, month: string): boolean {
  const last = lastDayOfMonth(month);
  const eff = toYmd(a.effectiveDate);
  const end = toYmd(a.endDate);
  if (eff && eff > last) return false;
  if (end && end < last) return false;
  return true;
}

/** Active as of a day; the end date itself is no longer active. */
export function isActiveOn(a: SummaryAssignment, ymd: string): boolean {
  const eff = toYmd(a.effectiveDate);
  const end = toYmd(a.endDate);
  if (eff && eff > ymd) return false;
  if (end && end <= ymd) return false;
  return true;
}

/**
 * Same-store average monthly growth (percent, 1 decimal) or null when no two
 * comparable months exist. Each consecutive month pair compares the total of
 * clients that were established by the earlier month (first full month after
 * onboarding) and had book in it; pair ratios are averaged geometrically.
 * e.g. one client with 0, 0, 1000, 1100 -> +10.0.
 */
export function computeAvgMonthlyGrowth(
  points: Array<{ clientId: number; month: string; amount: number }>,
  clientStartMonth: Map<number, string | null>,
): number | null {
  const valid = points.filter((p) => MONTH_RE.test(p.month));
  if (valid.length === 0) return null;

  const byMonth = new Map<string, Map<number, number>>();
  let first = valid[0].month;
  let last = valid[0].month;
  for (const p of valid) {
    if (p.month < first) first = p.month;
    if (p.month > last) last = p.month;
    const m = byMonth.get(p.month) ?? new Map<number, number>();
    m.set(p.clientId, (m.get(p.clientId) ?? 0) + p.amount);
    byMonth.set(p.month, m);
  }

  const ratios: number[] = [];
  for (let prev = first; prev < last; prev = addMonths(prev, 1)) {
    const cur = addMonths(prev, 1);
    const prevByClient = byMonth.get(prev);
    if (!prevByClient) continue;
    const curByClient = byMonth.get(cur);
    let prevTotal = 0;
    let curTotal = 0;
    prevByClient.forEach((amount, clientId) => {
      if (amount <= 0) return; // no book yet in the earlier month
      const start = clientStartMonth.get(clientId);
      if (start && start > prev) return; // not a full month yet
      prevTotal += amount;
      curTotal += curByClient?.get(clientId) ?? 0;
    });
    if (prevTotal > 0) ratios.push(curTotal / prevTotal);
  }
  if (ratios.length === 0) return null;

  const product = ratios.reduce((acc, r) => acc * r, 1);
  const avg = Math.pow(product, 1 / ratios.length) - 1;
  return Math.round(avg * 1000) / 10;
}

export function computeCommissionSummary(input: {
  month: string;
  asOf: string;
  records: SummaryRecord[];
  assignments: SummaryAssignment[];
  clients: SummaryClient[];
  splitProfiles: SummarySplitProfile[];
  /** Agent scope: the session user's ids in client_account_managers.user_id. */
  agentUserIds?: string[];
}): CommissionSummary {
  const { month, asOf, records, assignments, clients, splitProfiles } = input;
  const agentIds = input.agentUserIds ? new Set(input.agentUserIds.map(String)) : null;

  const clientById = new Map(clients.map((c) => [Number(c.id), c]));
  const agencyAgent = splitProfiles.filter((p) => p.splitType === "agency_agent");
  const primarySecondary = splitProfiles.filter((p) => p.splitType === "primary_secondary");
  const defaultAgencyAgent = agencyAgent.find((p) => p.isDefault);
  const defaultPrimarySecondary = primarySecondary.find((p) => p.isDefault);

  const producersByClient = new Map<number, SummaryAssignment[]>();
  for (const a of assignments) {
    if (!isProducer(a)) continue;
    const list = producersByClient.get(Number(a.clientId)) ?? [];
    list.push(a);
    producersByClient.set(Number(a.clientId), list);
  }

  const agentPctForClient = (clientId: number): number => {
    const splitId = clientById.get(clientId)?.agencyAgentSplitId;
    const profile =
      (splitId != null ? agencyAgent.find((p) => Number(p.id) === Number(splitId)) : undefined) ??
      defaultAgencyAgent;
    return profile ? parseMoney(profile.agentPercentage) : 50;
  };

  const primarySecondaryForClient = (clientId: number) => {
    const splitId = (producersByClient.get(clientId) ?? []).find((a) => a.primarySecondarySplitId != null)
      ?.primarySecondarySplitId;
    const profile =
      (splitId != null ? primarySecondary.find((p) => Number(p.id) === Number(splitId)) : undefined) ??
      defaultPrimarySecondary;
    return profile
      ? { primary: parseMoney(profile.primaryPercentage), secondary: parseMoney(profile.secondaryPercentage) }
      : { primary: 50, secondary: 50 };
  };

  // Per-record agent shares
  const shares: RecordShare[] = [];
  for (const r of records) {
    const m = recordMonth(r);
    if (!m) continue;
    const clientId = Number(r.clientId);
    const producers = (producersByClient.get(clientId) ?? []).filter((a) => wasActiveAtMonthEnd(a, m));
    if (producers.length === 0) continue;
    const amount = recordAmount(r);
    const agentPct = agentPctForClient(clientId);
    const split = producers.length > 1 ? primarySecondaryForClient(clientId) : null;
    const seen = new Set<string>();
    for (const p of producers) {
      const userId = String(p.userId);
      if (agentIds && !agentIds.has(userId)) continue;
      // Agent scope folds the user's ids into one; one share per record.
      const key = agentIds ? "me" : userId;
      if (seen.has(key)) continue;
      seen.add(key);
      const secondary = isSecondaryRole(p);
      const splitPct = split ? (secondary ? split.secondary : split.primary) : null;
      shares.push({
        recordId: Number(r.id),
        clientId,
        userId,
        month: m,
        // Rounded per record so any sum of shares (page totals, filtered
        // tables) adds up to the same cents.
        share: round2(amount * (agentPct / 100) * ((splitPct ?? 100) / 100)),
        agentPct,
        splitPct,
        isSecondary: secondary,
      });
    }
  }

  const activeProducerRows = assignments.filter((a) => isProducer(a) && isActiveOn(a, asOf));
  const clientStartMonth = new Map<number, string | null>(
    clients.map((c) => [Number(c.id), firstFullMonth(c.clientSince ?? c.createdAt)]),
  );

  const monthlyMap = new Map<string, number>();
  let totalCommissions = 0;
  let monthTotal = 0;
  let pendingTotal = 0;
  let monthRecordCount = 0;
  let growthPoints: Array<{ clientId: number; month: string; amount: number }>;

  if (agentIds) {
    const recordById = new Map(records.map((r) => [Number(r.id), r]));
    growthPoints = [];
    for (const s of shares) {
      const r = recordById.get(s.recordId)!;
      const factor = (s.agentPct / 100) * ((s.splitPct ?? 100) / 100);
      totalCommissions += s.share;
      monthlyMap.set(s.month, (monthlyMap.get(s.month) ?? 0) + s.share);
      growthPoints.push({ clientId: s.clientId, month: s.month, amount: s.share });
      if (s.month === month) {
        monthRecordCount += 1;
        monthTotal += round2(parseMoney(r.actualCommission) * factor);
        if (r.status === "pending") pendingTotal += round2(parseMoney(r.projectedCommission) * factor);
      }
    }
  } else {
    growthPoints = [];
    for (const r of records) {
      const m = recordMonth(r);
      if (!m) continue;
      const amount = recordAmount(r);
      totalCommissions += amount;
      monthlyMap.set(m, (monthlyMap.get(m) ?? 0) + amount);
      growthPoints.push({ clientId: Number(r.clientId), month: m, amount });
      if (m === month) {
        monthRecordCount += 1;
        monthTotal += parseMoney(r.actualCommission);
        if (r.status === "pending") pendingTotal += parseMoney(r.projectedCommission);
      }
    }
  }

  const agents: AgentSummary[] = [];
  if (!agentIds) {
    const byAgent = new Map<string, AgentSummary>();
    const ensure = (userId: string) => {
      let a = byAgent.get(userId);
      if (!a) {
        a = { userId, totalCommissions: 0, monthTotal: 0, recordCount: 0, assignedClientCount: 0 };
        byAgent.set(userId, a);
      }
      return a;
    };
    const recordById = new Map(records.map((r) => [Number(r.id), r]));
    for (const s of shares) {
      const a = ensure(s.userId);
      a.totalCommissions += s.share;
      a.recordCount += 1;
      if (s.month === month) {
        const factor = (s.agentPct / 100) * ((s.splitPct ?? 100) / 100);
        a.monthTotal += round2(parseMoney(recordById.get(s.recordId)?.actualCommission) * factor);
      }
    }
    const clientsByAgent = new Map<string, Set<number>>();
    for (const a of activeProducerRows) {
      const set = clientsByAgent.get(String(a.userId)) ?? new Set<number>();
      set.add(Number(a.clientId));
      clientsByAgent.set(String(a.userId), set);
    }
    clientsByAgent.forEach((set, userId) => {
      ensure(userId).assignedClientCount = set.size;
    });
    byAgent.forEach((a) => {
      agents.push({ ...a, totalCommissions: round2(a.totalCommissions), monthTotal: round2(a.monthTotal) });
    });
  }

  const myActiveRows = agentIds
    ? activeProducerRows.filter((a) => agentIds.has(String(a.userId)))
    : activeProducerRows;

  return {
    scope: agentIds ? "agent" : "agency",
    month,
    asOf,
    totalCommissions: round2(totalCommissions),
    monthTotal: round2(monthTotal),
    pendingTotal: round2(pendingTotal),
    monthRecordCount,
    assignedClientCount: new Set(myActiveRows.map((a) => Number(a.clientId))).size,
    activeAgentCount: new Set(myActiveRows.map((a) => String(a.userId))).size,
    avgMonthlyGrowthPct: computeAvgMonthlyGrowth(growthPoints, clientStartMonth),
    monthlyTotals: Array.from(monthlyMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([m, total]) => ({ month: m, total: round2(total) })),
    shares,
    agents,
  };
}

/** "+10.0%" / "-3.2%", or an em dash when there aren't two comparable months. */
export function formatGrowthPct(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return "—";
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
}
